/**
 * Articles Service
 * Handles all article-related API calls
 */

import { ApiClient } from '../core/api-client';
import { isApiError } from '../core/errors';
import type { Article } from '../../models/all';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

class ArticlesService {
  private readonly client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Get all articles
   */
  async getArticles(signal?: AbortSignal): Promise<Article[]> {
    try {
      const response = await this.client.get<Article[]>(
        '/articles',
        { signal },
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      if (isApiError(error) && (error.statusCode === 401 || error.statusCode === 403)) {
        // Gracefully handle auth errors
        return [];
      }
      // Return empty array on 404 or network errors
      return [];
    }
  }

  /**
   * Get a single article by ID
   */
  async getArticle(
    id: number | string,
    signal?: AbortSignal,
  ): Promise<Article | null> {
    try {
      const response = await this.client.get<Article>(
        `/articles/${id}`,
        { signal },
      );
      return response.data || null;
    } catch (error) {
      // Return null on error
      return null;
    }
  }
}

export const articlesService = new ArticlesService(ApiClient.getInstance(API_BASE_URL));

export type { Article };
